import React, { useState, useRef, useEffect } from "react";
import ReactMarkdown, { type Components } from "react-markdown";
import remarkGfm from "remark-gfm";
import remarkBreaks from "remark-breaks";
import { tokenizeLinks } from "../../utils/linkTokens";
import { renderRepHtml } from "../../utils/repHtml";
import LinkCard from "./LinkCard";
import SelectionCards from "./SelectionCards";
import TDGLinkCard from "./TDGLinkCard";
import TireBrowseCard from "./TireBrowseCard";
import TurnFeedback from "./TurnFeedback";
import PartialYmmvCard from "./PartialYmmvCard";
import YmmvSelectionCard from "./YmmvSelectionCard";
import ConversationSurveyCard, { type ConversationSurveyAnswers } from "./ConversationSurveyCard";
import { useTypingAnimation } from "../../hooks/useTypingAnimation";
import { getFriendlyToolName } from "../../utils/toolNameMapping";
import { addAnalyticsParam } from "../../utils/analytics";
import styles from "./MessageRenderer.module.css";
import chatStyles from "./ChatInterface.module.css";
import type { ToolCall, MessagePart, TireInfo, Option } from "../../types/chat.types";

// TRAC-732: headset glyph reused by the soft-handoff card.
const hsSvg = (size = 17) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}
       strokeLinecap="round" strokeLinejoin="round" style={{ display: 'block' }} aria-hidden="true">
    <path d="M4 14v-3a8 8 0 0 1 16 0v3" /><path d="M20 15v1.5a3.5 3.5 0 0 1-3.5 3.5H14" />
    <rect x="2" y="13.5" width="4" height="6" rx="1.6" /><rect x="18" y="13.5" width="4" height="6" rx="1.6" />
  </svg>
);

// TRAC-732: the SOFT-HANDOFF card. The agent explains WHY in its prose; this is just the
// mechanism — a 10s countdown that auto-connects to a live Tire Expert unless the customer
// connects now or opts to keep chatting with the AI.
//
// TRACI-865: the countdown used to run on requestAnimationFrame, which a browser stops in a
// background tab, so a shopper told "connecting you now" who looked away was never
// connected; and the card rendered nothing once it stopped being the latest message (the
// shopper typed, or a page change dropped it), so they were stranded with no button. Now:
//  - the deadline is the offer time (offeredAt) + SOFT_DURATION_MS, so a card restored after
//    a page change keeps the countdown it already had instead of starting a fresh one;
//  - one wall-clock setTimeout fires the connect (timers keep running, throttled, in a
//    background tab); returning to the tab settles it at once if the deadline passed;
//  - a deadline missed by more than SOFT_LATE_MS (the browser suspended the page, or the card
//    was restored long after the offer) does not connect someone; it pauses instead;
//  - a paused card (not the latest message, or late) says plainly they are not connected and
//    keeps a working Connect button.
const SOFT_DURATION_MS = 10000;
const SOFT_LATE_MS = 30000;
type SoftConnectReason = 'timer' | 'visibility' | 'click' | 'paused';
function SoftHandoffCard({ department, isInteractive, offeredAt, onCardSelect }: {
  department: string;
  isInteractive: boolean;
  offeredAt?: number;
  onCardSelect?: (value: string, label: string) => void;
}) {
  // Anchored to the offer, not to this mount (a remount must not grant a fresh 10 s).
  const deadlineRef = useRef<number | null>(offeredAt !== undefined ? offeredAt + SOFT_DURATION_MS : null);
  const [remaining, setRemaining] = useState(() =>
    Math.max(0, Math.ceil(((deadlineRef.current ?? Date.now() + SOFT_DURATION_MS) - Date.now()) / 1000)));
  const [acted, setActed] = useState(false); // clicked/timed-out — hide instantly; ChatInterface removes the card
  // Restored long after the offer, or the deadline was missed while suspended: no auto-connect.
  const [late, setLate] = useState(() => deadlineRef.current !== null && Date.now() - deadlineRef.current > SOFT_LATE_MS);
  const firedRef = useRef(false);

  const connect = (reason: SoftConnectReason) => {
    if (firedRef.current) return;
    firedRef.current = true;
    setActed(true);
    onCardSelect?.(`__soft_connect__:${department}`, reason); // ChatInterface escalates + removes the card
  };
  const connectRef = useRef(connect);
  connectRef.current = connect;

  const counting = isInteractive && !acted && !late;
  // The countdown → auto-connect, running only while this is the live (latest) card.
  useEffect(() => {
    if (!counting) return;
    const deadline = deadlineRef.current ?? (deadlineRef.current = Date.now() + SOFT_DURATION_MS);
    const settle = (reason: SoftConnectReason) => {
      const now = Date.now();
      if (now < deadline) return;
      if (now - deadline > SOFT_LATE_MS) { setLate(true); return; }
      connectRef.current(reason);
    };
    const timer = window.setTimeout(() => settle('timer'), Math.max(0, deadline - Date.now()));
    const tick = window.setInterval(() => {
      setRemaining(Math.max(0, Math.ceil((deadline - Date.now()) / 1000)));
    }, 1000);
    const onVisible = () => { if (document.visibilityState === 'visible') settle('visibility'); };
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      window.clearTimeout(timer);
      window.clearInterval(tick);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [counting]);

  if (acted) return null;
  const btnPrimary: React.CSSProperties = { flex: 1, border: 'none', borderRadius: 999, background: '#ce1126', color: '#fff', fontWeight: 700,
    fontSize: 13, padding: '10px 15px', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 7 };
  const btnSecondary: React.CSSProperties = { border: '1px solid #d9dfe5', background: '#fff', color: '#33425a', fontWeight: 700, fontSize: 13,
    borderRadius: 999, padding: '10px 14px', cursor: 'pointer', whiteSpace: 'nowrap' };

  if (!counting) {
    // TRACI-865: never vanish. The words above promised a connection; say plainly it has not
    // happened and keep the way through working.
    if (!onCardSelect) return null;
    return (
      <div data-testid="soft-handoff-paused" style={{ border: '1px solid #d9dfe5', borderRadius: 14, padding: 15, background: '#fff', maxWidth: 380 }}>
        <div style={{ display: 'flex', gap: 11, alignItems: 'flex-start' }}>
          <span style={{ color: '#ce1126', display: 'flex', marginTop: 1, flexShrink: 0 }}>{hsSvg(17)}</span>
          <div style={{ flex: 1 }}>
            <div style={{ fontWeight: 800, fontSize: 14.5, color: '#1b1d22', lineHeight: 1.35 }}>You’re not connected yet</div>
            <div style={{ fontSize: 12.5, color: '#8a8f98', marginTop: 4, lineHeight: 1.45 }}>Still want a Tire Expert? Tap Connect and a real person picks up right here.</div>
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 14 }}>
          <button onClick={() => connect('paused')} style={btnPrimary}>{hsSvg(16)}Connect now</button>
          <button onClick={() => { setActed(true); onCardSelect('__soft_dismiss__', 'dismiss'); }} style={btnSecondary}>No thanks</button>
        </div>
      </div>
    );
  }
  return (
    <div data-testid="soft-handoff-countdown" style={{ border: '1px solid #d9dfe5', borderRadius: 14, padding: 15, background: '#fff', maxWidth: 380 }}>
      <div style={{ display: 'flex', gap: 11, alignItems: 'flex-start' }}>
        <span style={{ color: '#ce1126', display: 'flex', marginTop: 1, flexShrink: 0 }}>{hsSvg(17)}</span>
        <div style={{ flex: 1 }}>
          <div style={{ fontWeight: 800, fontSize: 14.5, color: '#1b1d22', lineHeight: 1.35 }}>Connecting you to a Tire Expert</div>
          <div style={{ fontSize: 12.5, color: '#8a8f98', marginTop: 4, lineHeight: 1.45 }}>You’ll keep chatting right here — just with a real person.</div>
        </div>
      </div>
      <div style={{ height: 6, borderRadius: 999, background: '#f1f3f5', margin: '14px 0 12px', overflow: 'hidden' }}>
        {/* The fill is a CSS animation, so it needs no per-frame JavaScript. */}
        <div className={styles.softHandoffFill} style={{ animationDuration: `${SOFT_DURATION_MS}ms`,
          animationDelay: `${Math.min(0, (deadlineRef.current ?? Date.now() + SOFT_DURATION_MS) - SOFT_DURATION_MS - Date.now())}ms` }} />
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <button onClick={() => connect('click')} style={btnPrimary}>{hsSvg(16)}Connect now</button>
        <button onClick={() => { setActed(true); onCardSelect?.('__soft_cancel__', 'stay'); }} style={btnSecondary}>Stay with the AI</button>
      </div>
      <div style={{ textAlign: 'center', fontSize: 12, color: '#8a8f98', marginTop: 10 }}>Connecting in {remaining}s…</div>
    </div>
  );
}

// navigation_card eyebrow — a specific, customer-friendly label per destination `type`
// (from navigate_site's wayfinding map), instead of a generic "Tire Rack page".
const NAV_EYEBROW: Record<string, string> = {
  category: "Tire category",
  subcategory: "Tire category",
  taxonomy: "Tire category",
  brand: "Brand",
  education: "Tire guide",
  research: "Tire guide",
  tool: "Tire guide",
  parts: "Parts & accessories",
  support: "Help & support",
  account: "Your account",
  shopping: "Shop",
  shopping_entry: "Shop",
  deals: "Deals & rebates",
  payment: "Financing & payment",
  legal: "Policies",
  policy: "Policies",
  trust: "Policies",
  directory: "Directory",
  company: "About Tire Rack",
  hub: "Tire Rack",
};
const navEyebrow = (navType?: string): string =>
  NAV_EYEBROW[(navType || "").toLowerCase()] || "Tire Rack page";

interface MessageRendererProps {
  id?: string;
  content?: string;
  role: 'user' | 'assistant';
  parts?: MessagePart[];
  toolCalls?: ToolCall[];
  timestamp?: Date;
  isStreaming?: boolean;
  wasInterrupted?: boolean;
  formatTime: (timestamp: Date) => string;
  isLatestMessage: boolean;
  // TRAC-896: third arg is the button that was clicked. The parent uses it to
  // grow the vehicle picker out of whatever the customer actually tapped — it
  // has to be handed over at click time, since a chip/card can't be identified
  // afterwards (several may be on screen, and the tapped one ends up inside a
  // display:none subtree the moment the picker mounts). Ignored by every card
  // action that isn't a vehicle-picker entry.
  onCardSelect?: (value: string, label: string, origin?: HTMLElement | null) => void;
  // TRAC-896: tap on the partial YMMV card — the parent re-arms the
  // picker-resume refs from the part's payload and opens full-screen YMMV mode.
  onYmmvPreviewOpen?: (part: MessagePart, origin: HTMLElement | null, partIndex: number) => void;
  // TRAC-732: end-of-conversation survey — fires with accumulated answers after
  // each step. Wired only for the conversation_survey part.
  // TRAC-732: `messageId` identifies WHICH survey card answered, so the parent can persist
  // the answers back onto that card's part (rehydrates as answered/locked after a reload).
  onSurveySubmit?: (answers: ConversationSurveyAnswers & { messageId?: string }) => void;
  // TRAC-732: fires once when THIS (latest) message finishes streaming AND its
  // typewriter animation catches up — used to defer the soft-handoff card until
  // the agent's reply is fully typed (so it doesn't get shoved down mid-stream).
  onTypingSettled?: () => void;
  sessionId?: string | null;
  turnNumber?: number;
  feedback?: number | null;
  feedbackText?: string;
  feedbackId?: string;
  // TRAC-518 Phase 8a follow-up: Langfuse trace ID from the turn's
  // response_complete; forwarded to TurnFeedback so submissions skip the
  // backend's DDB lookup.
  traceId?: string;
  // TRAC-631: Langfuse deep-link URL emitted alongside traceId, present
  // only for internal users. Drives the copy-trace-link icon button in
  // TurnFeedback.
  langfuseTraceUrl?: string;
  // Error handling props
  hasError?: boolean;
  errorDetails?: string;
  onRetry?: () => void;
  // Session expiry notification (TRAC-189)
  isSessionExpiry?: boolean;
  // TRAC-732: live-human handoff — message / status / timeline divider / recap card
  responder?: 'human' | 'system' | 'divider' | 'summary' | 'connecting';
  secureFormNote?: 'sent' | 'submitted';
  agentName?: string;
  agentAvatar?: string;
  richHtml?: string;
  dividerKind?: 'enter-human' | 'end-human' | 'back-to-ai';
  dividerPending?: boolean;
  summaryKind?: 'to-human' | 'from-human';
  inHumanZone?: boolean;
}

/**
 * Strips incomplete markdown syntax from the end of a string to prevent
 * raw markdown characters from being displayed during streaming animation.
 *
 * This handles cases like:
 * - "Hello **wor" -> "Hello " (incomplete bold)
 * - "Check [this" -> "Check " (incomplete link)
 * - "Use `code" -> "Use " (incomplete inline code)
 */
const stripIncompleteMarkdown = (text: string): string => {
  if (!text) return text;

  let result = text;

  // Track if we're inside incomplete markdown constructs by scanning backwards
  // We need to find the safe truncation point

  // 1. Handle incomplete inline code (single backtick)
  const backtickCount = (result.match(/`/g) || []).length;
  if (backtickCount % 2 !== 0) {
    // Odd number of backticks - find the last unmatched one and truncate there
    const lastBacktick = result.lastIndexOf('`');
    if (lastBacktick !== -1) {
      result = result.slice(0, lastBacktick);
    }
  }

  // 2. Handle incomplete bold/italic (**text or *text or __text or _text)
  // Look for unmatched emphasis markers at the end

  // Check for trailing asterisks or underscores that aren't paired
  // Count ** pairs (bold) - each ** should be paired
  const doubleStar = result.split('**').length - 1;
  if (doubleStar % 2 !== 0) {
    // Odd number of ** - incomplete bold, find last ** and truncate
    const lastDoubleStar = result.lastIndexOf('**');
    if (lastDoubleStar !== -1) {
      result = result.slice(0, lastDoubleStar);
    }
  }

  // Check for single * (italic) - but not part of **
  // Replace ** temporarily to count single *
  const withoutDoubleStar = result.replace(/\*\*/g, '');
  const singleStarCount = (withoutDoubleStar.match(/\*/g) || []).length;
  if (singleStarCount % 2 !== 0) {
    // Find the last unmatched * that's not part of **
    // Scan backwards to find it
    let starDepth = 0;
    let truncateAt = -1;
    for (let i = result.length - 1; i >= 0; i--) {
      if (result[i] === '*') {
        // Check if it's part of **
        if (i > 0 && result[i - 1] === '*') {
          // Part of **, skip both
          i--;
          continue;
        }
        if (i < result.length - 1 && result[i + 1] === '*') {
          // Part of **, already handled
          continue;
        }
        // Single *, toggle depth
        if (starDepth === 0) {
          truncateAt = i;
          break;
        }
      }
    }
    if (truncateAt !== -1) {
      result = result.slice(0, truncateAt);
    }
  }

  // Same for underscores (__ and _)
  const doubleUnderscore = result.split('__').length - 1;
  if (doubleUnderscore % 2 !== 0) {
    const lastDoubleUnderscore = result.lastIndexOf('__');
    if (lastDoubleUnderscore !== -1) {
      result = result.slice(0, lastDoubleUnderscore);
    }
  }

  const withoutDoubleUnderscore = result.replace(/__/g, '');
  const singleUnderscoreCount = (withoutDoubleUnderscore.match(/_/g) || []).length;
  if (singleUnderscoreCount % 2 !== 0) {
    let truncateAt = -1;
    for (let i = result.length - 1; i >= 0; i--) {
      if (result[i] === '_') {
        if (i > 0 && result[i - 1] === '_') {
          i--;
          continue;
        }
        if (i < result.length - 1 && result[i + 1] === '_') {
          continue;
        }
        truncateAt = i;
        break;
      }
    }
    if (truncateAt !== -1) {
      result = result.slice(0, truncateAt);
    }
  }

  // 3. Handle incomplete links [text](url)
  // Look for [ without matching ]( or without complete )
  const openBrackets = (result.match(/\[/g) || []).length;
  const closedLinks = (result.match(/\]\([^)]*\)/g) || []).length;
  if (openBrackets > closedLinks) {
    // Find the last unmatched [
    let bracketDepth = 0;
    for (let i = result.length - 1; i >= 0; i--) {
      if (result[i] === ')') {
        // Check if this closes a link
        const linkMatch = result.slice(0, i + 1).match(/\]\([^)]*\)$/);
        if (linkMatch) {
          // Skip past the matched link
          const linkStart = result.lastIndexOf('[', i);
          if (linkStart !== -1) {
            i = linkStart;
          }
        }
      } else if (result[i] === '[') {
        // Found an unmatched [
        result = result.slice(0, i);
        break;
      }
    }
  }

  // 4. Handle incomplete strikethrough ~~text
  const tilde = result.split('~~').length - 1;
  if (tilde % 2 !== 0) {
    const lastTilde = result.lastIndexOf('~~');
    if (lastTilde !== -1) {
      result = result.slice(0, lastTilde);
    }
  }

  return result;
};

/**
 * ToolCallIndicator - Shows tool execution status with pulsing dot (running) or checkmark (completed)
 */
const ToolCallIndicator: React.FC<{ toolCall: ToolCall }> = ({ toolCall }) => {
  // No internal hide logic — parent controls visibility entirely via conditional rendering.

  const getStatusIcon = (): React.ReactNode => {
    switch (toolCall.status) {
      case "running":
        return (
          <span className={styles.toolPulseContainer}>
            <span className={styles.toolPulse}></span>
          </span>
        );
      case "completed":
        return (
          <span className={styles.toolPulseContainer}>
            <span className={styles.toolPulse}></span>
          </span>
        );
      case "error":
        return <span className={styles.toolErrorIcon}>✕</span>;
      default:
        return null;
    }
  };

  const getStatusClass = (): string => {
    switch (toolCall.status) {
      case "running":
        return styles.toolRunning;
      case "completed":
        return styles.toolCompleted;
      case "error":
        return styles.toolError;
      default:
        return "";
    }
  };

  // Generate a human-readable summary of the tool call
  const getToolSummary = (): string => {
    const params = toolCall.parameters || {};
    const toolName = toolCall.tool_name;

    switch (toolName) {
      case "get_vehicle_fitment": {
        // Progressive lookup - display based on which parameters are provided
        const brand = (params.brand as string) || "";
        const year = (params.year as string) || "";
        const model = (params.model as string) || "";
        const variant = (params.variant as string) || "";

        if (variant && model && year) {
          // Level 4: Full vehicle spec - tire sizes
          return `Found tire sizes for ${year} ${brand} ${model} ${variant}`.trim();
        } else if (model && year) {
          // Level 3: Variants lookup
          return `Retrieved variants for ${year} ${brand} ${model}`.trim();
        } else if (year) {
          // Level 2: Models lookup
          return `Found models for ${brand} in ${year}`;
        } else {
          // Level 1: Years lookup
          return `Searched for available years for ${brand}`;
        }
      }

      case "get_tire_variants_by_model": {
        const manufacturer = (params.manufacturer as string) || "";
        const model = (params.model as string) || "";
        return `Retrieved variants for ${manufacturer} ${model}`.trim();
      }

      case "get_articles_by_section": {
        const section = (params.section as string) || "articles";
        return `Found articles in section: ${section}`;
      }

      case "get_tire_test_by_title": {
        const title = (params.title as string) || "tire tests";
        const titlePreview =
          title.length > 50 ? title.substring(0, 50) + "..." : title;
        const sortBy = (params.sort_by as string) || "publish_date";
        return `Searched for: "${titlePreview}" (sorted by ${sortBy})`;
      }

      case "query_tire_knowledge_base": {
        const query = (params.query as string) || "tire information";
        const preview =
          query.length > 60 ? query.substring(0, 60) + "..." : query;
        return `Searched knowledge base: "${preview}"`;
      }

      default:
        // Fallback: show formatted parameters
        if (Object.keys(params).length > 0) {
          const paramStr = Object.entries(params)
            .map(([key, value]) => `${key}: ${JSON.stringify(value)}`)
            .join(", ");
          return `Parameters: ${paramStr}`;
        }
        return toolCall.result_summary || "Completed";
    }
  };

  return (
    <div className={`${styles.toolIndicator} ${getStatusClass()}`}>
      <div className={styles.toolHeader}>
        {getStatusIcon()}
        <div className={styles.toolContent}>
          <span className={styles.toolName}>
            {toolCall.description || getFriendlyToolName(toolCall.tool_name)}
          </span>
        </div>
      </div>
      {toolCall.progress_message && (
        <div className={styles.toolProgress}>{toolCall.progress_message}</div>
      )}
    </div>
  );
};

/**
 * CollapsibleSection - Reusable collapsible container with fade effect
 * Shows content with a fade gradient when collapsed, full content when expanded
 */
interface CollapsibleSectionProps {
  children: React.ReactNode;
  /** Height in pixels when collapsed */
  collapsedHeight?: number;
  /** Max height when expanded (for scrollable content) */
  expandedMaxHeight?: number;
  expandedLabel?: string;
  collapsedLabel?: string;
  defaultExpanded?: boolean;
  /** Header text - if provided, clicking header toggles instead of bottom button */
  header?: string;
  /** Badge text shown next to header (e.g., count) */
  headerBadge?: string;
  className?: string;
}

const CollapsibleSection: React.FC<CollapsibleSectionProps> = ({
  children,
  collapsedHeight = 120,
  expandedMaxHeight,
  expandedLabel = 'Show less',
  collapsedLabel = 'See details',
  defaultExpanded = false,
  header,
  headerBadge,
  className = '',
}) => {
  const [isExpanded, setIsExpanded] = useState(defaultExpanded);

  const contentStyle: React.CSSProperties = {
    '--collapsed-height': `${collapsedHeight}px`,
    ...(expandedMaxHeight && isExpanded ? { maxHeight: `${expandedMaxHeight}px`, overflowY: 'auto' } : {}),
  } as React.CSSProperties;

  return (
    <div className={`${styles.collapsibleSection} ${className}`}>
      {/* Header mode - clickable header with chevron */}
      {header && (
        <button
          className={styles.collapsibleHeader}
          onClick={() => setIsExpanded(!isExpanded)}
          aria-expanded={isExpanded}
        >
          <span className={styles.collapsibleHeaderText}>
            {header}
            {headerBadge && <span className={styles.collapsibleHeaderBadge}>{headerBadge}</span>}
          </span>
          <span className={`${styles.collapsibleChevron} ${isExpanded ? styles.collapsibleChevronOpen : ''}`}>
            ▼
          </span>
        </button>
      )}
      {/* Collapsible content */}
      <div
        className={`${styles.collapsibleContent} ${!isExpanded ? styles.collapsibleContentCollapsed : ''}`}
        style={contentStyle}
      >
        {children}
      </div>
      {/* Bottom toggle - only shown when no header */}
      {!header && (
        <button
          className={`${styles.collapsibleToggle} ${!isExpanded ? styles.collapsibleToggleCollapsed : ''}`}
          onClick={() => setIsExpanded(!isExpanded)}
        >
          {isExpanded ? expandedLabel : collapsedLabel}
        </button>
      )}
    </div>
  );
};

/**
 * TireCard - Expandable tire information card with ratings, specs, and pricing
 */
const TireCard: React.FC<{
  tire: TireInfo;
  title?: string;
  turnNumber: number;
  position: number;
}> = ({ tire, title, turnNumber, position }) => {

  if (!tire) return null;

  // Helper to render star rating
  const renderStars = (rating: number) => {
    const fullStars = Math.floor(rating / 2); // Convert 10-scale to 5-star
    const hasHalfStar = (rating / 2) % 1 >= 0.25 && (rating / 2) % 1 < 0.75;
    const emptyStars = 5 - fullStars - (hasHalfStar ? 1 : 0);

    return (
      <span className={styles.tireCardStars}>
        {'★'.repeat(fullStars)}
        {hasHalfStar && <span className={styles.halfStar}>★</span>}
        {'☆'.repeat(emptyStars)}
      </span>
    );
  };

  // Determine which price to show
  const displayPrice = tire.price || tire.priceRange;

  const cardContent = (
    <div className={styles.tireCardCollapsed}>
      <div className={styles.tireCardHeader}>
        <div className={styles.tireCardTitleBlock}>
          <span className={styles.tireCardBrand}>{tire.manufacturer}</span>
          <span className={styles.tireCardTitle}>{tire.modelName}</span>
          {tire.tireType && (
            <span className={styles.tireCardType}>{tire.tireType}</span>
          )}
        </div>
      </div>

      {/* Rating and Price */}
      <div className={styles.tireCardSummary}>
        {tire.overallRating && (
          <div className={styles.tireCardRatingSummary}>
            {renderStars(tire.overallRating)}
            <span className={styles.tireCardRatingValue}>
              {(tire.overallRating / 2).toFixed(1)}
            </span>
          </div>
        )}
        {displayPrice && (
          <span className={styles.tireCardPrice}>{displayPrice}</span>
        )}
      </div>
    </div>
  );

  const tireLabel = [tire.manufacturer, tire.modelName].filter(Boolean).join(' ').trim();

  return (
    <div className={styles.tireCardPart}>
      {tire.productUrl ? (
        <a
          href={addAnalyticsParam(tire.productUrl)}
          target="_blank"
          rel="noopener noreferrer"
          className={styles.tireCard}
          style={{ textDecoration: 'none' }}
          data-track-id="tire_card"
          data-track-turn={turnNumber}
          data-track-option={tireLabel}
          data-track-extra={JSON.stringify({
            productUrl: tire.productUrl,
            position,
          })}
        >
          {cardContent}
        </a>
      ) : (
        <div className={styles.tireCard}>
          {cardContent}
        </div>
      )}
    </div>
  );
};

/**
 * SourceCardsPart - one group of source cards (e.g. a tire's test cards). Shows
 * the first 2 cards (for v2 test groups the backend pins the headline test first)
 * and collapses any extras behind a dropdown. Collapse is count-based so it works
 * for ANY card type (tests, articles, PDPs, or a mix); the copy is type-aware so a
 * pure tire-test group reads "tests" and mixed/non-test groups use a generic label.
 */
const SOURCE_CARDS_VISIBLE = 2;

const SourceCardsPart: React.FC<{ title?: string; options?: Option[]; turnNumber: number }> = ({
  title,
  options = [],
  turnNumber,
}) => {
  const [expanded, setExpanded] = useState(false);
  const gridRef = useRef<HTMLDivElement>(null);

  // Collapse ANY group with more than 2 cards — tests, articles, PDPs, or a mix.
  // allTests drives only the COPY (so a non-test card is never called a "test"),
  // not whether we collapse.
  const allTests =
    options.length > 0 && options.every((o) => o.subtitle === "Tire Test");
  const collapsible = options.length > SOURCE_CARDS_VISIBLE;
  const hiddenCount = collapsible ? options.length - SOURCE_CARDS_VISIBLE : 0;

  // Bring revealed cards into view on expand (the embed iframe / small screens
  // scroll an internal container, so new cards can land below the fold).
  useEffect(() => {
    if (expanded && gridRef.current) {
      gridRef.current.scrollIntoView({ block: "nearest" });
    }
  }, [expanded]);

  return (
    <div
      className={styles.sourceCardsPart}
      data-track-id="source_card"
      data-track-turn={turnNumber}
    >
      {title && <div className={styles.uiCardTitle}>{title}</div>}
      <div className={styles.sourceCardsGrid} ref={gridRef}>
        {options.map((option, optIdx) => {
          // Overflow cards stay in the DOM (display:none) so analytics
          // collectOptions records the full options_shown and clicks on revealed
          // cards correlate to an impression that listed them.
          const hidden = collapsible && !expanded && optIdx >= SOURCE_CARDS_VISIBLE;
          const trackedUrl = addAnalyticsParam(option.value);
          return (
            <a
              key={optIdx}
              href={trackedUrl}
              target="_blank"
              rel="noopener noreferrer"
              className={styles.sourceCard}
              style={hidden ? { display: "none" } : undefined}
              data-track-option={option.label}
              data-track-extra={JSON.stringify({ url: trackedUrl })}
            >
              <span className={styles.sourceCardLabel}>{option.label}</span>
              {option.subtitle && (
                <span className={styles.sourceCardSubtitle}>{option.subtitle}</span>
              )}
            </a>
          );
        })}
      </div>
      {collapsible && (
        <button
          type="button"
          className={styles.sourceCardsToggle}
          aria-expanded={expanded}
          onClick={() => setExpanded((v) => !v)}
        >
          <span>
            {expanded
              ? allTests
                ? "Show fewer tests"
                : "Show fewer"
              : allTests
                ? `Show ${hiddenCount} more test${hiddenCount === 1 ? "" : "s"}`
                : `Show ${hiddenCount} more`}
          </span>
          <span
            aria-hidden="true"
            className={`${styles.collapsibleChevron} ${expanded ? styles.collapsibleChevronOpen : ""}`}
          >
            ▼
          </span>
        </button>
      )}
    </div>
  );
};

/**
 * MessageRenderer - Renders chat messages with formatting
 */

// TRAC-1078 follow-up: the markdown renderers live at module scope so their
// identity is stable across renders. Defined inline, every re-render handed
// react-markdown a fresh component type, React remounted the whole tree, and
// a table the reader had scrolled sideways snapped back to its left edge (seen
// on dev at 375px: the scroll box was recreated about once a second).
const STATIC_MARKDOWN_COMPONENTS: Components = {
  // Customize rendering for specific elements
  p: ({ children }) => <p className={styles.paragraph}>{children}</p>,
  ul: ({ children }) => (
    <ul className={styles.bulletList}>{children}</ul>
  ),
  ol: ({ children }) => (
    <ol className={styles.orderedList}>{children}</ol>
  ),
  li: ({ children }) => <li className={styles.listItem}>{children}</li>,
  code: (props) => {
    // react-markdown's own Components type has no `inline`; kept as the
    // renderer always read it, so behavior is unchanged by the hoist.
    const { inline, children } = props as typeof props & { inline?: boolean };
    return inline ? (
      <code className={styles.inlineCode}>{children}</code>
    ) : (
      <code className={styles.codeBlock}>{children}</code>
    );
  },
  pre: ({ children }) => <pre className={styles.pre}>{children}</pre>,
  blockquote: ({ children }) => (
    <blockquote className={styles.blockquote}>{children}</blockquote>
  ),
  h1: ({ children }) => <h1 className={styles.h1}>{children}</h1>,
  h2: ({ children }) => <h2 className={styles.h2}>{children}</h2>,
  h3: ({ children }) => <h3 className={styles.h3}>{children}</h3>,
  h4: ({ children }) => <h4 className={styles.h4}>{children}</h4>,
  // TRAC-1078: a table wider than the chat panel scrolls sideways inside
  // its own box (like .pre does for code) instead of being clipped.
  table: ({ children }) => (
    <div className={styles.tableScroll}>
      <table className={styles.table}>{children}</table>
    </div>
  ),
  thead: ({ children }) => (
    <thead className={styles.thead}>{children}</thead>
  ),
  tbody: ({ children }) => (
    <tbody className={styles.tbody}>{children}</tbody>
  ),
  tr: ({ children }) => <tr className={styles.tr}>{children}</tr>,
  th: ({ children }) => <th className={styles.th}>{children}</th>,
  td: ({ children }) => <td className={styles.td}>{children}</td>,
  strong: ({ children }) => (
    <strong className={styles.strong}>{children}</strong>
  ),
  em: ({ children }) => <em className={styles.em}>{children}</em>,
};

const makeLinkRenderer = (effectiveTurnNumber: number): Components["a"] =>
  ({ children, href }) => {
    // Add analytics tracking parameter to TireRack.com links
    const finalHref = href && href.includes('tirerack.com')
      ? addAnalyticsParam(href)
      : href;

    const linkType = href
      ? href.includes('TireSearchResults')
        ? 'search_results'
        : href.includes('/tires/')
          ? 'category_browse'
          : 'product'
      : undefined;

    // ChatTrackingRoot reads these to emit `inline_link`
    // impression + click. `data-track-defer-impression` keeps
    // the pre-refactor behavior of creating the impression only
    // when the link is actually clicked (not on render).
    return href ? (
      <a
        className={styles.link}
        href={finalHref}
        target="_blank"
        rel="noopener noreferrer"
        data-track-id="inline_link"
        data-track-turn={effectiveTurnNumber}
        data-track-option={href}
        data-track-extra={JSON.stringify({
          url: finalHref || href,
          linkType,
        })}
        data-track-defer-impression="true"
      >
        {children}
      </a>
    ) : (
      <a
        className={styles.link}
        href={finalHref}
        target="_blank"
        rel="noopener noreferrer"
      >
        {children}
      </a>
    );
  };

const MessageRenderer: React.FC<MessageRendererProps> = ({
  id,
  content,
  role,
  parts,
  toolCalls,
  timestamp,
  isStreaming,
  wasInterrupted,
  formatTime,
  isLatestMessage,
  onCardSelect,
  onYmmvPreviewOpen,
  onSurveySubmit,
  onTypingSettled,
  // Feedback props
  sessionId,
  turnNumber,
  feedback,
  feedbackText,
  feedbackId,
  traceId,
  langfuseTraceUrl,
  // Error handling props
  hasError,
  errorDetails,
  onRetry,
  // Session expiry notification (TRAC-189)
  isSessionExpiry,
  // TRAC-732: live-human handoff attribution
  responder,
  secureFormNote,
  agentName,
  agentAvatar,
  richHtml,
  dividerKind,
  dividerPending,
  summaryKind,
  inHumanZone,
}) => {
  // TRAC-527: Interaction tracking is handled by ChatTrackingRoot via
  // data-track-id / data-track-option / data-track-turn attributes on
  // the elements below. Per-turn identity for impressions/clicks rides
  // on data-track-turn; effectiveTurnNumber mirrors the pre-refactor
  // rule of using -1 for client-only messages (welcome, session expiry).
  const effectiveTurnNumber = turnNumber ?? -1;

  // Only the link renderer reads a prop; everything else is the module-level
  // set, so the table (and every other element) keeps its DOM node between
  // renders.
  const markdownComponents = React.useMemo<Components>(
    () => ({ ...STATIC_MARKDOWN_COMPONENTS, a: makeLinkRenderer(effectiveTurnNumber) }),
    [effectiveTurnNumber],
  );

  /**
   * Pre-process text to fix common markdown formatting issues
   * - Converts emoji-prefixed lines to proper markdown list items
   */
  const preprocessMarkdown = (text: string): string => {
    if (!text) return text;

    // Regex to match lines that start with an emoji (not already a list item)
    // This handles emojis at the start of a line that should be list items
    const emojiLineRegex = /^(?![-*+]\s)(\p{Emoji_Presentation}|\p{Emoji}\uFE0F)\s+(.+)$/gmu;

    // Split into lines, process, and rejoin
    const lines = text.split('\n');
    let inEmojiList = false;
    const processedLines = lines.map((line, index) => {
      const trimmedLine = line.trim();

      // Check if this line starts with an emoji (and isn't already a list item)
      if (emojiLineRegex.test(trimmedLine)) {
        emojiLineRegex.lastIndex = 0; // Reset regex state
        inEmojiList = true;
        return `- ${trimmedLine}`;
      }

      // Reset emoji list state if we hit an empty line or different content
      if (trimmedLine === '' || (!trimmedLine.match(/^[-*+]\s/) && !emojiLineRegex.test(trimmedLine))) {
        emojiLineRegex.lastIndex = 0;
        inEmojiList = false;
      }

      return line;
    });

    return processedLines.join('\n');
  };

  /**
   * Format text with markdown rendering
   */
  const formatText = (text: string): React.ReactNode => {
    if (!text) return null;

    // Pre-process to fix emoji lists and other formatting issues
    const processedText = preprocessMarkdown(text);

    // Render markdown with react-markdown
    return (
      <ReactMarkdown
        // remarkBreaks renders a single newline as a line break, the way chat UIs
        // (Slack/Discord/GitHub comments) do. Plain markdown collapses single
        // newlines into one paragraph, which silently ran the agent's three-line
        // test verdict (Summary / What We Liked / What We'd Improve) together as
        // one blob. Handling it here keeps line-break semantics out of the agent's
        // prompt — its newlines mean what they look like.
        remarkPlugins={[remarkGfm, remarkBreaks]}
        components={markdownComponents}
      >
        {processedText}
      </ReactMarkdown>
    );
  };

  // The parts as they RENDER, not as they streamed (memoized: this feeds the
  // typing-animation hook below, so it must be derived before it).
  //
  // 1. TDG text-cutoff: drop transitional text before a tdg_link_card.
  // 2. Shop-card interleave (Dylan, 8/12): a shop card renders BETWEEN the
  //    sentence that announces it and any follow-up prose — when a
  //    multi-paragraph text part is immediately followed by a shop card, the
  //    text splits at its first paragraph break and the card seats there.
  //    Streaming is unaffected: the card event arrives after the prose chunks,
  //    so the split only exists once the card does — and because the animation
  //    hook consumes THIS list, the typing buffer holds the tail slice, never
  //    the pre-split whole.
  const renderParts = React.useMemo<MessagePart[] | undefined>(() => {
    if (!parts || parts.length === 0) return parts;
    const tdgCardIndex = parts.findIndex((p) => p.type === "tdg_link_card");
    const tdgToolIndex = tdgCardIndex < 0
      ? parts.findIndex((p) => p.type === "tool" && p.toolCall?.tool_name === "generate_tdg_link")
      : -1;
    const tdgCutoffIndex = tdgCardIndex >= 0 ? tdgCardIndex : tdgToolIndex;
    const base = tdgCutoffIndex > 0
      ? parts.filter((p, i) => !(i < tdgCutoffIndex && p.type === "text"))
      : parts;
    const SHOP_CARD_TYPES = new Set(["tire_browse_card", "tdg_link_card", "size_search_link_card"]);
    const out: MessagePart[] = [];
    for (let i = 0; i < base.length; i++) {
      const p = base[i];
      const next = base[i + 1];
      if (p.type === "text" && next && SHOP_CARD_TYPES.has(next.type)) {
        const content = p.content || "";
        const brk = content.search(/\n{2,}/);
        if (brk > 0) {
          out.push({ ...p, content: content.slice(0, brk) });
          out.push(next);
          out.push({ ...p, content: content.slice(brk).replace(/^\n+/, "") });
          i++; // the card was consumed
          continue;
        }
      }
      out.push(p);
    }
    return out;
  }, [parts]);

  // Find the last text part index for animation (modern format with parts) —
  // over renderParts, so the animated "last text" is the piece that actually
  // renders last (the tail slice when a shop card interleaved).
  const lastTextPartIndex = renderParts
    ? renderParts.map((p, i) => (p.type === "text" ? i : -1)).filter((i) => i >= 0).pop() ?? -1
    : -1;

  // Get the content of the last text part for animation (modern format)
  const lastTextPartContent =
    lastTextPartIndex >= 0 && renderParts ? renderParts[lastTextPartIndex]?.content || "" : "";

  // Use typing animation for the last text part (only animates during/after streaming)
  const {
    displayedContent: animatedLastDisplayed,
    newContent: animatedLastNew,
    isAnimating: isLastTextAnimating,
  } = useTypingAnimation({
    content: lastTextPartContent,
    isStreaming: isStreaming,
    speed: 20,
    charsPerBatch: 3,
  });

  // Use typing animation for legacy format (direct content prop)
  const {
    displayedContent: legacyDisplayed,
    newContent: legacyNew,
    isAnimating: isLegacyAnimating,
  } = useTypingAnimation({
    content: content || "",
    isStreaming: isStreaming,
    speed: 20,
    charsPerBatch: 3,
  });

  // Combined animated content for rendering
  const animatedLastTextContent = animatedLastDisplayed + animatedLastNew;
  const animatedLegacyContent = legacyDisplayed + legacyNew;

  // Check if animation is still in progress (not caught up to content)
  const isAnimationInProgress = isLastTextAnimating || isLegacyAnimating;

  // TRAC-732: signal ChatInterface once THIS latest message has both finished
  // streaming (network) AND finished typing (animation), so a deferred soft-handoff
  // card can appear cleanly below it instead of being pushed down mid-stream.
  const typingSettledFiredRef = useRef(false);
  useEffect(() => {
    if (!isLatestMessage || isStreaming || isAnimationInProgress) {
      typingSettledFiredRef.current = false; // re-arm for the next stream/animation cycle
      return;
    }
    if (typingSettledFiredRef.current) return;
    typingSettledFiredRef.current = true;
    onTypingSettled?.();
  }, [isLatestMessage, isStreaming, isAnimationInProgress, onTypingSettled]);

  // Render using parts if available (modern format)
  if (renderParts && renderParts.length > 0) {
    // Dylan (8/12): the sources block ("Related reading") always renders at the
    // BOTTOM of the turn, whatever order the stream delivered it in — a source
    // list sitting above another card reads like a citation for the wrong
    // element. Stable partition, so everything else keeps its stream order.
    // Note render order now deliberately diverges from parts-array order, which
    // is why the YMMV card below identifies itself by parts.indexOf, never by
    // its position in this render list.
    const filteredParts = [
      ...renderParts.filter((p) => p.type !== "source_cards"),
      ...renderParts.filter((p) => p.type === "source_cards"),
    ];

    // Recompute last text index for filteredParts (original lastTextPartIndex is for unfiltered parts)
    const lastFilteredTextIdx = filteredParts
      .map((p, i) => (p.type === "text" ? i : -1)).filter((i) => i >= 0).pop() ?? -1;

    // Check if last part is text (for cursor display)
    let lastPartIsText = false;
    filteredParts.forEach((part) => {
      lastPartIsText = part.type === "text";
    });

    // Check if we should show waiting indicator (bouncing dots) when caught up but still streaming
    const isWaitingForContent = isStreaming && !isAnimationInProgress && lastPartIsText;

    // While streaming, check if any part will produce visible content (text, UI cards, running tools).
    // If not, keep completed tool indicators visible so the bubble doesn't go empty.
    const hasVisibleContent = !isStreaming || filteredParts.some((part) => {
      if (part.type === "text" && part.content?.trim()) return true;
      if (part.type === "tool") return false; // tools don't count — checked separately
      if (part.type !== "text") return true; // UI cards (tdg_link_card, selection_cards, etc.)
      return false;
    });

    // Group consecutive tire_card parts for 3-column grid rendering
    type RenderGroup =
      | { type: 'single'; part: typeof filteredParts[0]; idx: number }
      | { type: 'tire_grid'; cards: { part: typeof filteredParts[0]; idx: number }[] };

    const renderGroups: RenderGroup[] = filteredParts.reduce((acc: RenderGroup[], part, idx) => {
      if (part.type === 'tire_card') {
        const last = acc[acc.length - 1];
        if (last?.type === 'tire_grid') {
          last.cards.push({ part, idx });
        } else {
          acc.push({ type: 'tire_grid', cards: [{ part, idx }] });
        }
      } else {
        acc.push({ type: 'single', part, idx });
      }
      return acc;
    }, []);

    return (
      <>
        <div className={chatStyles.messageContent}>
          <div className={chatStyles.messageText}>
            <div className={styles.messageContent}>
              {renderGroups.map((group, groupIdx) => {
                if (group.type === 'tire_grid') {
                  // Check if any card in the group should wait for preceding text animation
                  const shouldWait = group.cards.some(({ idx }) => {
                    const precedingTextPartIdx = filteredParts
                      .slice(0, idx)
                      .map((p, i) => (p.type === "text" ? i : -1))
                      .filter((i) => i >= 0)
                      .pop();
                    return (
                      precedingTextPartIdx !== undefined &&
                      precedingTextPartIdx === lastFilteredTextIdx &&
                      isLastTextAnimating
                    );
                  });
                  if (shouldWait) return null;
                  return (
                    <div key={`tg-${groupIdx}`} className={styles.tireCardGrid}>
                      {group.cards.map(({ part, idx }, cardIdx) => (
                        <TireCard
                          key={idx}
                          tire={part.tire as TireInfo}
                          title={part.title}
                          turnNumber={effectiveTurnNumber}
                          position={cardIdx}
                        />
                      ))}
                    </div>
                  );
                }

                const { part, idx } = group;
                // Find the most recent text part before this part
                const precedingTextPartIdx = filteredParts
                  .slice(0, idx)
                  .map((p, i) => (p.type === "text" ? i : -1))
                  .filter((i) => i >= 0)
                  .pop();

                // Check if we should delay rendering this non-text part
                // Wait for preceding text animation to complete before showing tool calls
                const shouldWaitForPrecedingText =
                  part.type !== "text" &&
                  precedingTextPartIdx !== undefined &&
                  precedingTextPartIdx === lastFilteredTextIdx &&
                  isLastTextAnimating;

                if (part.type === "text") {
                  // Skip empty text parts so they don't create gap spacing.
                  // TRAC-396: previously the "last empty post-stream" case was
                  // allowed to render, which produced a 0-height textPart div
                  // that the parent flex (gap: 24px) treated as a real sibling,
                  // adding 24px of dead space above the next UI part (e.g.,
                  // size_search_link_card). Always skip empty text parts.
                  const isLastTextPart = idx === lastFilteredTextIdx;
                  if (!part.content?.trim()) {
                    return null;
                  }
                  // Render all animated content through markdown to preserve structure
                  // (splitting content breaks markdown lists and other structures)
                  const fullAnimatedContent = isLastTextPart
                    ? animatedLastDisplayed + animatedLastNew
                    : part.content || "";
                  // Strip incomplete markdown during animation to prevent raw syntax from showing
                  const safeContent = isLastTextPart && isLastTextAnimating
                    ? stripIncompleteMarkdown(fullAnimatedContent)
                    : fullAnimatedContent;
                  const textElement = formatText(safeContent);
                  const isLastPart = idx === filteredParts.length - 1;
                  return (
                    <div
                      key={idx}
                      className={`${styles.textPart} ${
                        role === "user" ? styles.userTextPart : ""
                      }`}
                    >
                      {textElement}
                      {isWaitingForContent && isLastPart && (
                        <span className={styles.inlineTypingIndicator}>
                          <span className={styles.inlineTypingDot}></span>
                          <span className={styles.inlineTypingDot}></span>
                          <span className={styles.inlineTypingDot}></span>
                        </span>
                      )}
                      {wasInterrupted && isLastPart && (
                        <span className={styles.interruptedIndicator}>
                          ...{" "}
                          <span className={styles.interruptedText}>
                            (response interrupted)
                          </span>
                        </span>
                      )}
                    </div>
                  );
                }

                // Delay rendering non-text parts while preceding text is still animating
                if (shouldWaitForPrecedingText) {
                  return null;
                }

                // Show tool indicators inline in chronological order
                if (part.type === "tool") {
                  const toolName = part.toolCall?.tool_name || "";
                  // Always hide UI display tools - they produce visible UI themselves.
                  // TRAC-732: route_to_human / connect_to_specialist have no spinner
                  // either — the "Connecting you now…" handoff UX takes over instead.
                  if (
                    toolName.startsWith("show_") ||
                    toolName === "redirect_to_sales" ||
                    toolName === "route_to_human" ||
                    toolName === "connect_to_specialist"
                  ) {
                    return null;
                  }

                  // TRAC-291: Some tools produce a specific UI part (e.g., generate_tdg_link → tdg_link_card).
                  // The spinner stays visible until that part physically exists in the message.
                  const TOOL_WAITS_FOR_PART: Record<string, string> = {
                    "generate_tdg_link": "tdg_link_card",
                  };
                  const awaitedPartType = TOOL_WAITS_FOR_PART[toolName];
                  const awaitedPartExists = awaitedPartType
                    ? filteredParts.some((p) => p.type === awaitedPartType)
                    : false;

                  // Decision: should we show this tool indicator?
                  // YES if: still running, OR waiting for its result part
                  // NO if: completed AND (result arrived OR no awaited part and other content visible)
                  if (part.toolCall?.status === "completed") {
                    if (awaitedPartType && !awaitedPartExists) {
                      // Waiting for specific result (e.g., tdg_link_card) — keep showing
                    } else {
                      // Result arrived or no specific result to wait for — hide
                      return null;
                    }
                  }

                  // Running tools show the full processing indicator (tire + cycling text)
                  // — same UI as the initial "thinking" state in ChatInterface
                  return (
                    <div key={`tool-${part.toolCall?.id || idx}`} className={chatStyles.processingIndicator}>
                      <div className={chatStyles.tireAnimation}>
                        <svg className={chatStyles.tireSvg} viewBox="0 0 100 100" xmlns="http://www.w3.org/2000/svg">
                          <circle cx="50" cy="50" r="45" fill="none" stroke="#2b7088" strokeWidth="8" className={chatStyles.tireOuter} />
                          <g className={chatStyles.treadPattern}>
                            {[...Array(12)].map((_, i) => (
                              <line key={i} x1="50" y1="5" x2="50" y2="12" stroke="#2b7088" strokeWidth="3" strokeLinecap="round" transform={`rotate(${i * 30} 50 50)`} />
                            ))}
                          </g>
                          <circle cx="50" cy="50" r="18" fill="#2b7088" className={chatStyles.tireHub} />
                          <g className={chatStyles.hubSpokes}>
                            {[...Array(5)].map((_, i) => (
                              <circle key={i} cx={50 + 10 * Math.cos((i * 72 * Math.PI) / 180)} cy={50 + 10 * Math.sin((i * 72 * Math.PI) / 180)} r="3" fill="#ffffff" />
                            ))}
                          </g>
                          <circle cx="50" cy="50" r="5" fill="#ffffff" />
                        </svg>
                      </div>
                      <span className={chatStyles.processingText}>
                        {part.toolCall?.description || getFriendlyToolName(part.toolCall?.tool_name || '')}
                      </span>
                    </div>
                  );
                }

                if (part.type === "selection_cards") {
                  // Cards are interactive only if:
                  // 1. This is the latest message
                  // 2. User hasn't selected a value yet (single) or selectedValues is empty (multi)
                  // 3. onCardSelect handler is provided
                  const hasSelection = !!part.selectedValue;
                  const isInteractive =
                    isLatestMessage && !hasSelection && onCardSelect;

                  return (
                    <div
                      key={idx}
                      className={styles.selectionCardsPart}
                      data-track-id="selection_card"
                      data-track-turn={effectiveTurnNumber}
                    >
                      {part.title && (
                        <div className={styles.uiCardTitle}>{part.title}</div>
                      )}
                      <SelectionCards
                        options={part.options || []}
                        selectedValue={part.selectedValue}
                        onSelect={isInteractive ? onCardSelect : undefined}
                        disabled={!isInteractive}
                      />
                      {part.hint && (
                        <div className={styles.selectionCardsHint}>{part.hint}</div>
                      )}
                    </div>
                  );
                }

                if (part.type === "user_selection_card") {
                  return (
                    <div key={idx} className={styles.userSelectionCardPart}>
                      <div className={styles.selectedCard}>
                        <span className={styles.selectedLabel}>{part.selectedLabel}</span>
                      </div>
                    </div>
                  );
                }

                if (part.type === "source_cards") {
                  return (
                    <SourceCardsPart
                      key={idx}
                      title={part.title}
                      options={part.options}
                      turnNumber={effectiveTurnNumber}
                    />
                  );
                }

                if (part.type === "ranking_cards") {
                  return (
                    <div key={idx} className={styles.rankingCardsPart}>
                      {part.title && (
                        <div className={styles.uiCardTitle}>{part.title}</div>
                      )}
                      <div className={styles.rankingCardsList}>
                        {part.options?.map((option, optIdx) => (
                          <div key={optIdx} className={styles.rankingCard}>
                            <span className={styles.rankingPosition}>
                              {optIdx + 1}
                            </span>
                            <div className={styles.rankingContent}>
                              <span className={styles.rankingLabel}>
                                {option.label}
                              </span>
                              {option.subtitle && (
                                <span className={styles.rankingSubtitle}>
                                  {option.subtitle}
                                </span>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                }

                if (part.type === "rating_display") {
                  return (
                    <div key={idx} className={styles.ratingDisplayPart}>
                      {part.title && (
                        <div className={styles.uiCardTitle}>{part.title}</div>
                      )}
                      <div className={styles.ratingOverview}>
                        <div className={styles.ratingScore}>
                          <span className={styles.ratingNumber}>
                            {part.overallRating}
                          </span>
                          <span className={styles.ratingMax}>/10</span>
                        </div>
                        {part.reviewCount && part.reviewCount > 0 && (
                          <span className={styles.reviewCount}>
                            Based on {part.reviewCount.toLocaleString()} reviews
                          </span>
                        )}
                      </div>
                      {part.metrics && part.metrics.length > 0 && (
                        <div className={styles.ratingMetrics}>
                          {part.metrics.map((metric, metricIdx) => (
                            <div
                              key={metricIdx}
                              className={styles.ratingMetric}
                            >
                              <span className={styles.metricLabel}>
                                {metric.label}
                              </span>
                              <span className={styles.metricValue}>
                                {metric.subtitle}
                              </span>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                }

                if (part.type === "tdg_link_card") {
                  // TDG cards are interactive only if:
                  // 1. This is the latest message
                  // 2. onCardSelect handler is provided
                  const isTdgInteractive = isLatestMessage && onCardSelect;

                  return (
                    <TDGLinkCard
                      key={idx}
                      part={part}
                      onChipSelect={isTdgInteractive ? onCardSelect : undefined}
                      isInteractive={!!isTdgInteractive}
                      turnNumber={effectiveTurnNumber}
                    />
                  );
                }

                // Tire-browse card (find_tires, TRAC-295): one card for any tire-browse
                // link — size search, category page, or no-results fallback. Renders the title.
                if (part.type === "tire_browse_card") {
                  if (!part.searchUrl || !(part.title || part.tireSize)) return null;
                  // TRAC-902 display mitigation (Dylan, 8/12): a staggered pick can
                  // reach this card with the tool's raw combined string as its title
                  // ("285/35R23 front / 325/30R23 rear"). Present it in the same
                  // F:/R: convention as the vehicle bar and the selection card —
                  // TireBrowseCard already renders that shape. Display only; the
                  // underlying single-size URL is TRAC-902's agent-side fix.
                  const stagMatch = /^(\S+)\s*\(?front\)?\s*\/\s*(\S+)\s*\(?rear\)?$/i.exec(
                    part.title || part.tireSize || "",
                  );
                  return (
                    <TireBrowseCard
                      key={idx}
                      title={stagMatch ? undefined : part.title}
                      tireSize={stagMatch ? undefined : part.tireSize}
                      isStaggered={!!stagMatch}
                      frontTireSize={stagMatch ? stagMatch[1] : undefined}
                      rearTireSize={stagMatch ? stagMatch[2] : undefined}
                      searchUrl={part.searchUrl}
                      ctaText={part.ctaText}
                    />
                  );
                }

                // Legacy size-search card (browse-by-size / staggered redirect, TRAC-396).
                if (part.type === "size_search_link_card") {
                  if (!part.searchUrl || !part.tireSize) return null;
                  return (
                    <TireBrowseCard
                      key={idx}
                      tireSize={part.tireSize}
                      searchUrl={part.searchUrl}
                      ctaText={part.ctaText}
                      isStaggered={part.isStaggered}
                      frontTireSize={part.frontTireSize}
                      rearTireSize={part.rearTireSize}
                    />
                  );
                }

                if (part.type === "follow_up_actions") {
                  // Follow-up actions are interactive only if:
                  // 1. This is the latest message
                  // 2. onCardSelect handler is provided
                  const isInteractive = isLatestMessage && onCardSelect;
                  // TRAC-478: Selected value (if any) identifies which prompt the user
                  // clicked. Set on the committed welcome so reload shows the same choice.
                  const selectedValue = part.selectedValue;
                  // TRAC-478: The grid defaults to opacity:0 so pre-conversation
                  // measurement can slice the prompt list without a flash. Past/committed
                  // welcomes need no measurement — apply the visible class directly.
                  const gridClass = isInteractive
                    ? styles.followUpActionsGrid
                    : `${styles.followUpActionsGrid} ${styles.followUpActionsGridVisible}`;

                  return (
                    <div
                      key={idx}
                      className={styles.followUpActionsPart}
                      data-track-id="starter_prompt"
                      data-track-turn={effectiveTurnNumber}
                    >
                      {part.title && (
                        <div className={styles.uiCardTitle}>{part.title}</div>
                      )}
                      <div className={gridClass}>
                        {part.actions?.map((action, actIdx) => {
                          const isSelected = selectedValue != null && action.value === selectedValue;
                          const buttonClass = [
                            styles.followUpActionButton,
                            isInteractive && styles.followUpActionButtonActive,
                            isSelected && styles.followUpActionButtonSelected,
                            !isInteractive && !isSelected && styles.followUpActionDisabled,
                          ].filter(Boolean).join(' ');
                          return (
                            <button
                              key={actIdx}
                              className={buttonClass}
                              onClick={(e) => {
                                if (isInteractive && onCardSelect) {
                                  // TRAC-896: this is the cold-open starter-prompt
                                  // chip — hand over the chip itself so the picker
                                  // can grow out of it.
                                  onCardSelect(action.label, action.value, e.currentTarget);
                                }
                              }}
                              title={action.description || ''}
                              disabled={!isInteractive}
                              data-track-option={action.label}
                            >
                              <span className={styles.followUpActionLabel}>{action.label}</span>
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  );
                }

                // TRAC-732: routing_actions — the department choice buttons under the
                // canned "which team?" agent message. Unlike follow_up_actions (whose grid
                // starts opacity:0 and is revealed by the welcome-only measurePrompts pass),
                // these are ALWAYS visible; interactive only while this is the latest message.
                // Clicking routes the customer to that department (see handleCardSelect,
                // which intercepts the "__route_to__:<dept>" value).
                if (part.type === "routing_actions") {
                  const isInteractive = isLatestMessage && !!onCardSelect;
                  return (
                    <div key={idx} className={styles.followUpActionsPart}>
                      <div className={`${styles.followUpActionsGrid} ${styles.followUpActionsGridVisible} ${styles.routingActionsGrid}`}>
                        {part.actions?.map((action, actIdx) => (
                          <button
                            key={actIdx}
                            className={[
                              styles.followUpActionButton,
                              isInteractive ? styles.followUpActionButtonActive : styles.followUpActionDisabled,
                              styles.routingActionButton,
                            ].filter(Boolean).join(' ')}
                            onClick={() => { if (isInteractive && onCardSelect) onCardSelect(action.value, action.label); }}
                            disabled={!isInteractive}
                            data-track-option={action.label}
                          >
                            {/* headset — the same mark the live-chat header wears, so every
                                door out to a person carries one consistent signal. */}
                            <svg className={styles.routingActionIcon} viewBox="0 0 24 24" fill="none"
                                 stroke="currentColor" strokeWidth="2" strokeLinecap="round"
                                 strokeLinejoin="round" aria-hidden>
                              <path d="M4 14v-3a8 8 0 0 1 16 0v3" />
                              <path d="M20 15v1.5a3.5 3.5 0 0 1-3.5 3.5H14" />
                              <rect x="2" y="13.5" width="4" height="6" rx="1.6" />
                              <rect x="18" y="13.5" width="4" height="6" rx="1.6" />
                            </svg>
                            <span className={styles.followUpActionLabel}>{action.label}</span>
                          </button>
                        ))}
                      </div>
                      {part.hint && (
                        // Same rendering as a normal agent text part (styles.textPart +
                        // formatText) so it reads conversational and identical to the
                        // "Happy to connect you…" line — just with a little space above.
                        // 20px here cost a line of the sentence itself in a 400x600 bubble.
                        <div className={styles.textPart} style={{ marginTop: 14 }}>
                          {formatText(part.hint)}
                        </div>
                      )}
                    </div>
                  );
                }

                // TRAC-732: soft-handoff — the 10s "connecting you to a Tire Expert"
                // countdown card. Counts only while it's the latest message; otherwise it
                // shows the paused "not connected yet" state (TRACI-865).
                if (part.type === "soft_handoff") {
                  return (
                    <div key={idx} style={{ marginTop: 4 }}>
                      <SoftHandoffCard
                        department={(part as { department?: string }).department || 'Customer Care'}
                        isInteractive={isLatestMessage && !!onCardSelect}
                        offeredAt={part.offeredAt}
                        onCardSelect={onCardSelect}
                      />
                    </div>
                  );
                }

                // TRAC-732: end-of-conversation survey card. Stays interactive
                // regardless of isLatestMessage — the AI's comeback recap lands
                // after it, so it's rarely the latest message but must still work.
                if (part.type === "conversation_survey") {
                  if (!onSurveySubmit) return null;
                  return (
                    <div key={idx} style={{ marginTop: 4 }}>
                      <ConversationSurveyCard
                        // TRAC-732: tag each answer with THIS card's message id so the
                        // parent can persist the state back onto this exact survey part.
                        onSubmit={(answers) => onSurveySubmit({ ...answers, messageId: id })}
                        department={part.department}
                        initial={part.surveyState}
                      />
                    </div>
                  );
                }

                // TRAC-232: ymmv_interface is now handled at ChatInterface level (full-area overlay)
                if (part.type === "ymmv_interface") {
                  return null;
                }

                // TRAC-896: partial YMMV card — inline, tappable preview
                // slice of the Find Your Vehicle interface. Sits alongside the turn's
                // answer text and other cards; tapping opens the real full-screen
                // picker. Stays tappable in history (not gated on isLatestMessage) —
                // the customer can come back to it turns later.
                if (part.type === "ymmv_preview_card") {
                  return (
                    // The parts container is a flex column with a 24px row-gap, so
                    // this negative margin nets the prose→card gap to 16px — TIGHTER
                    // than the generic gap between sibling parts, because this card
                    // is the answer to the sentence directly above it and should
                    // read as attached to it (Miles, 8/12: 32px read as detached).
                    <div key={idx} style={{ marginTop: -8 }}>
                      <PartialYmmvCard
                        // TRAC-896: same isLatestMessage gate every other interactive
                        // card here uses — a picker sitting in old history is a stale
                        // prompt, and the vehicle header bar is the persistent way to
                        // change vehicle. `idx` identifies WHICH card was opened, so a
                        // completed selection writes back to this one and not to a
                        // newer card that streamed in meanwhile.
                        onOpen={
                          isLatestMessage && onYmmvPreviewOpen
                            ? (origin) => onYmmvPreviewOpen(part, origin, parts.indexOf(part))
                            : undefined
                        }
                        prefill={part.prefill}
                        turnNumber={effectiveTurnNumber}
                      />
                    </div>
                  );
                }

                // TRAC-896 completion experiment ('user-card' arm): the customer's
                // finished selection, sitting in their own slot like a sent answer.
                if (part.type === "ymmv_selection_card") {
                  return (
                    <div key={idx} style={{ alignSelf: "flex-end" }}>
                      <YmmvSelectionCard vehicle={part.vehicleDescription || ""} tireSize={part.tireSize} />
                    </div>
                  );
                }

                // navigate_site: page-link (wayfinding) card. One clickable card that
                // sends the customer to a real, verified Tire Rack page from the curated
                // wayfinding map. Reuses the source-card anchor styling so it reads as the
                // same "here's a link" card family. Renders after the prose as its own block.
                //
                // NOTE: we render only the destination `title` (the page name) + a fixed
                // neutral subtitle. The leaf's `when_to_use` is an INTERNAL matching/trigger
                // note (carries dev jargon + cross-references like "route_to_human:CustomerCare")
                // and must NOT be shown to the customer — the model's relay sentence already
                // describes what the page is for.
                if (part.type === "navigation_card") {
                  if (!part.url || !part.title) return null;
                  // Renders through the shared LinkCard so a wayfinding link reads in the
                  // SAME format as tire_browse_card (eyebrow + page name + CTA button), not
                  // the old source-card link row.
                  return (
                    <LinkCard
                      key={idx}
                      url={part.url}
                      eyebrow={navEyebrow(part.navType)}
                      label={part.title}
                      ctaText="Visit page →"
                      trackId="navigation_card"
                      trackOption={part.title}
                      trackTurn={effectiveTurnNumber}
                    />
                  );
                }

                // Legacy tire_info_card (backwards compatibility)
                if (part.type === "tire_info_card") {
                  const tire = part.tire;
                  if (!tire) return null;

                  return (
                    <div key={idx} className={styles.tireInfoCardPart}>
                      {part.title && (
                        <div className={styles.uiCardTitle}>{part.title}</div>
                      )}
                      <div className={styles.tireInfoCard}>
                        <div className={styles.tireInfoHeader}>
                          <span className={styles.tireInfoIcon}>🛞</span>
                          <div className={styles.tireInfoTitleBlock}>
                            <span className={styles.tireInfoTitle}>
                              {tire.manufacturer} {tire.modelName}
                            </span>
                            {tire.tireType && (
                              <span className={styles.tireInfoType}>{tire.tireType}</span>
                            )}
                          </div>
                        </div>
                        {tire.specs && Object.keys(tire.specs).length > 0 && (
                          <div className={styles.tireInfoSpecs}>
                            {tire.specs.speed_rating && (
                              <div className={styles.tireSpecItem}>
                                <span className={styles.tireSpecLabel}>Speed Rating</span>
                                <span className={styles.tireSpecValue}>{String(tire.specs.speed_rating)}</span>
                              </div>
                            )}
                            {tire.specs.load_index && (
                              <div className={styles.tireSpecItem}>
                                <span className={styles.tireSpecLabel}>Load Index</span>
                                <span className={styles.tireSpecValue}>{String(tire.specs.load_index)}</span>
                              </div>
                            )}
                            {tire.specs.treadwear && (
                              <div className={styles.tireSpecItem}>
                                <span className={styles.tireSpecLabel}>Treadwear</span>
                                <span className={styles.tireSpecValue}>{String(tire.specs.treadwear)}</span>
                              </div>
                            )}
                            {tire.specs.warranty_miles && (
                              <div className={styles.tireSpecItem}>
                                <span className={styles.tireSpecLabel}>Warranty</span>
                                <span className={styles.tireSpecValue}>{Number(tire.specs.warranty_miles).toLocaleString()} mi</span>
                              </div>
                            )}
                          </div>
                        )}
                        {tire.sizesAvailable && tire.sizesAvailable.length > 0 && (
                          <div className={styles.tireInfoSizes}>
                            <span className={styles.tireInfoSizesLabel}>Available Sizes:</span>
                            <div className={styles.tireInfoSizesList}>
                              {tire.sizesAvailable.slice(0, 6).map((size, sizeIdx) => (
                                <span key={sizeIdx} className={styles.tireInfoSizeChip}>{size}</span>
                              ))}
                              {tire.sizesAvailable.length > 6 && (
                                <span className={styles.tireInfoSizeMore}>+{tire.sizesAvailable.length - 6} more</span>
                              )}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  );
                }

                return null;
              })}
            </div>
          </div>
          {timestamp && (
            <div className={chatStyles.messageTime}>
              {formatTime(timestamp)}
            </div>
          )}
        </div>
        {/* Turn feedback for assistant messages - only shown once streaming is done.
            TRAC-732: NOT for handoff messages (responder set = live human / system / divider /
            summary) — you don't thumbs-rate the LP rep or a "transferring…" line. */}
        {role === 'assistant' && !responder && !isStreaming && sessionId && turnNumber !== undefined && (
          <TurnFeedback
            sessionId={sessionId}
            turnNumber={turnNumber}
            initialFeedback={feedback}
            initialFeedbackText={feedbackText}
            feedbackId={feedbackId}
            traceId={traceId}
            langfuseTraceUrl={langfuseTraceUrl}
          />
        )}
      </>
    );
  }

  // TRAC-732: timeline divider — the band's top ("Chat with Joshua started") and
  // bottom ("Back with TRACI") edges, each with a timestamp.
  if (responder === 'divider') {
    if (dividerKind === 'back-to-ai') {
      // back-to-ai: lives OUTSIDE the red band, in the normal AI flow, as an EXACT
      // twin of the top-of-band "Connected ✅" turn (a user message: 24px / 700 /
      // #111). No divider line above it, no timestamp — same vibe as Connected ✅.
      return (
        <div style={{ fontSize: 24, fontWeight: 700, color: '#111', lineHeight: 1.25, margin: '16px 0 8px 0' }}>
          Back with the AI tire agent ✅
        </div>
      );
    }
    // In-band edges of the red card: enter-human opens it ("Chat with X started",
    // line BELOW the label) and end-human closes it ("Chat with X ended", line
    // ABOVE the label) — a top/bottom mirror, both red.
    const enter = dividerKind === 'enter-human';
    return (
      <div style={{ width: '100%', ...(enter
        ? { borderBottom: '1px solid #f0d2d7', paddingBottom: 12, marginBottom: 6 }
        : { borderTop: '1px solid #f0d2d7', paddingTop: 12, marginTop: 6 }) }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{ fontSize: 15, fontWeight: 800, color: '#ce1126' }}>
            {enter
              ? (dividerPending
                  ? 'While we wait'
                  : `Chat with ${agentName || 'a Tire Expert'} started`)
              : `Chat with ${agentName || 'a Tire Expert'} ended`}
          </span>
        </div>
        {/* No timestamp while still connecting — there's no event to stamp yet. */}
        {timestamp && !dividerPending && (
          <div style={{ fontSize: 11, color: '#9aa3ad', fontWeight: 600, marginTop: 12 }}>{formatTime(timestamp)}</div>
        )}
      </div>
    );
  }

  // TRAC-732: recap card — "Shared with Joshua" (in) / "Recap from Joshua → TRACI" (out)
  if (responder === 'summary') {
    const toHuman = summaryKind === 'to-human';
    return (
      <div style={{ background: '#fff', border: `1px solid ${toHuman ? '#f0d2d7' : '#cdd3da'}`, borderRadius: 10, padding: '10px 12px', margin: '10px 0', fontSize: 12.5, color: '#444', lineHeight: 1.5 }}>
        <div style={{ fontSize: 10.5, fontWeight: 800, letterSpacing: 0.4, marginBottom: 5, color: toHuman ? '#ce1126' : '#2b7088' }}>
          {toHuman ? `↪ SHARED WITH ${(agentName || 'THE TIRE EXPERT').toUpperCase()}` : `↩ RECAP FROM ${(agentName || 'THE TIRE EXPERT').toUpperCase()} → TRACI`}
        </div>
        {content}
      </div>
    );
  }

  // TRACI-1012: payment-form status line (sent / submitted) inside the live chat
  if (secureFormNote) {
    const done = secureFormNote === 'submitted';
    return (
      <div role="status" style={{ display: 'flex', alignItems: 'center', gap: 10, margin: '8px 0', padding: '10px 12px', borderRadius: 10, background: done ? '#e3f1e8' : '#e3f0f4', color: done ? '#1f4d31' : '#1d4a5a', fontSize: 14.5, lineHeight: 1.4, fontWeight: 600 }}>
        {done ? (
          <svg width="20" height="20" viewBox="0 0 20 20" aria-hidden="true" style={{ flex: 'none' }}><circle cx="10" cy="10" r="9" fill="#2f7a4f" /><path d="M6 10.5l2.6 2.6L14 7.8" stroke="#fff" strokeWidth="2" fill="none" /></svg>
        ) : (
          <svg width="18" height="18" viewBox="0 0 16 16" aria-hidden="true" style={{ flex: 'none' }}><rect x="2.5" y="7" width="11" height="7.5" rx="1.5" fill="#2b7088" /><path d="M5 7V5a3 3 0 0 1 6 0v2" stroke="#2b7088" strokeWidth="1.6" fill="none" /></svg>
        )}
        <span>{content}</span>
      </div>
    );
  }

  // TRAC-732: handoff status — left-aligned, sits in the chat flow (no centered pill)
  if (responder === 'system') {
    return (
      <div style={{ fontSize: 12.5, color: '#6b7480', lineHeight: 1.5, margin: '8px 0', padding: '0 2px' }}>
        {content}
      </div>
    );
  }

  // TRAC-732: live-human message — red avatar blip + name + message. The band
  // (rail + faint-red bg) comes from the message wrapper, so this stays slim.
  if (responder === 'human') {
    return (
      <div style={{ padding: '2px 0' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 2 }}>
          {agentAvatar ? (
            <img src={agentAvatar} alt="" style={{ width: 26, height: 26, borderRadius: '50%', objectFit: 'cover', border: '1.5px solid #ce1126' }} />
          ) : (
            <span style={{ width: 26, height: 26, borderRadius: '50%', background: '#ce1126', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, fontWeight: 700 }}>
              {(agentName || 'T').trim().charAt(0).toUpperCase()}
            </span>
          )}
          <span style={{ fontSize: 12, fontWeight: 800, color: '#ce1126' }}>{agentName || 'Tire Rack team'}</span>
        </div>
        {timestamp && (
          <div style={{ fontSize: 11, color: '#9aa3ad', fontWeight: 600, margin: '12px 0 9px' }}>{formatTime(timestamp)}</div>
        )}
        {richHtml ? (
          // The rep used LivePerson's formatting toolbar: render the sanitized-HTML twin
          // (bold, italic, underline, lists, links) through a strict element allowlist.
          <div className={styles.repRichText} style={{ fontSize: 16, lineHeight: 1.55, color: '#1b1d22', overflowWrap: 'anywhere' }}>
            {renderRepHtml(richHtml)}
          </div>
        ) : (
        /* Plain rep text is NOT markdown (see linkTokens.ts) — only links become anchors,
           same target/rel as the AI's links so a PDP opens beside the chat. */
        <div style={{ fontSize: 16, lineHeight: 1.55, color: '#1b1d22', whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>
          {tokenizeLinks(content).map((t, i) =>
            t.kind === 'link' ? (
              <a key={i} className={styles.link} href={t.href} target="_blank" rel="noopener noreferrer">
                {t.text}
              </a>
            ) : (
              <React.Fragment key={i}>{t.text}</React.Fragment>
            ),
          )}
        </div>
        )}
      </div>
    );
  }

  // Fallback to legacy format - render all animated content through markdown
  // (splitting content breaks markdown lists and other structures)
  // Strip incomplete markdown during animation to prevent raw syntax from showing
  const safeLegacyContent = isLegacyAnimating
    ? stripIncompleteMarkdown(animatedLegacyContent)
    : animatedLegacyContent;
  const textElement = formatText(safeLegacyContent);

  // Check if we should show waiting indicator (bouncing dots) when caught up but still streaming
  const hasToolCalls = toolCalls && toolCalls.length > 0;
  const legacyAnimationInProgress = animatedLegacyContent.length < (content || "").length;
  const isWaitingForContentLegacy = isStreaming && !legacyAnimationInProgress && !hasToolCalls;

  // If this is a session expiry notification, render special styled message (TRAC-189)
  if (isSessionExpiry && role === 'assistant') {
    return (
      <div className={chatStyles.messageContent}>
        <div className={chatStyles.messageText}>
          <div className={styles.sessionExpiryNotice}>
            <div className={styles.sessionExpiryIcon}>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="10" />
                <polyline points="12,6 12,12 16,14" />
              </svg>
            </div>
            <span className={styles.sessionExpiryText}>
              {content || 'Your session has expired due to inactivity. Starting fresh.'}
            </span>
          </div>
        </div>
      </div>
    );
  }

  // If this is an error message, render the error bubble instead
  if (hasError && role === 'assistant') {
    return (
      <>
        <div className={chatStyles.messageContent}>
          <div className={chatStyles.messageText}>
            <div className={styles.errorBubble}>
              <div className={styles.errorHeader}>
                <div className={styles.errorIcon}>
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <circle cx="12" cy="12" r="10" />
                    <line x1="12" y1="8" x2="12" y2="12" />
                    <line x1="12" y1="16" x2="12.01" y2="16" />
                  </svg>
                </div>
                <span className={styles.errorFriendlyMessage}>
                  Something went wrong while processing your request.
                </span>
              </div>
              {errorDetails && (
                <div className={styles.errorDetails}>
                  {errorDetails}
                </div>
              )}
              {onRetry && (
                <div className={styles.errorActions}>
                  <button className={styles.retryButton} onClick={onRetry}>
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <polyline points="23,4 23,10 17,10" />
                      <path d="M20.49,15a9,9,0,1,1-2.12-9.36L23,10" />
                    </svg>
                    Try again
                  </button>
                </div>
              )}
            </div>
          </div>
          {timestamp && (
            <div className={chatStyles.messageTime}>{formatTime(timestamp)}</div>
          )}
        </div>
      </>
    );
  }

  return (
    <>
    <div className={chatStyles.messageContent}>
      <div className={chatStyles.messageText}>
        <div className={styles.messageContent}>
          {toolCalls && toolCalls.length > 0 && (
            <div className={styles.toolCallsSection}>
              {toolCalls
                .filter((toolCall) => !toolCall.tool_name?.startsWith("show_"))
                .map((toolCall) => (
                  <ToolCallIndicator key={toolCall.id} toolCall={toolCall} />
                ))}
            </div>
          )}
          <div
            className={`${styles.textPart} ${
              role === "user" ? styles.userTextPart : ""
            }`}
          >
            {textElement}
            {isWaitingForContentLegacy && (
              <span className={styles.inlineTypingIndicator}>
                <span className={styles.inlineTypingDot}></span>
                <span className={styles.inlineTypingDot}></span>
                <span className={styles.inlineTypingDot}></span>
              </span>
            )}
            {wasInterrupted && (
              <span className={styles.interruptedIndicator}>
                ...{" "}
                <span className={styles.interruptedText}>
                  (response interrupted)
                </span>
              </span>
            )}
          </div>
        </div>
      </div>
      {/* TRAC-732: the customer's in-band messages get the visible band timestamp below; every
          other message gets the (globally hidden) .messageTime slot. Mutually exclusive so a
          band message can never render BOTH — a belt against a duplicate timestamp if the
          .messageTime display:none ever fails to apply. */}
      {timestamp && !(role === 'user' && inHumanZone) && (
        <div className={chatStyles.messageTime}>{formatTime(timestamp)}</div>
      )}
      {/* TRAC-732: visible timestamp on the customer's messages inside the human band */}
      {role === 'user' && inHumanZone && timestamp && (
        <div style={{ fontSize: 11, color: '#9aa3ad', fontWeight: 600, marginTop: 3 }}>{formatTime(timestamp)}</div>
      )}
    </div>
    {/* Turn feedback for assistant messages - shown immediately to avoid jarring pop-in (TRAC-218).
        TRAC-732: excluded for handoff messages (responder set = live human / system). */}
    {role === 'assistant' && !responder && sessionId && turnNumber !== undefined && (
      <TurnFeedback
        sessionId={sessionId}
        turnNumber={turnNumber}
        initialFeedback={feedback}
        initialFeedbackText={feedbackText}
        feedbackId={feedbackId}
        traceId={traceId}
        langfuseTraceUrl={langfuseTraceUrl}
      />
    )}
    </>
  );
};

export default React.memo(MessageRenderer, (prev, next) => {
  // Fast path: if content/streaming state changed, always re-render
  if (prev.content !== next.content) return false;
  if (prev.isStreaming !== next.isStreaming) return false;
  if (prev.parts !== next.parts) return false;
  if (prev.toolCalls !== next.toolCalls) return false;
  if (prev.feedback !== next.feedback) return false;
  if (prev.feedbackText !== next.feedbackText) return false;
  if (prev.hasError !== next.hasError) return false;
  if (prev.wasInterrupted !== next.wasInterrupted) return false;
  if (prev.isLatestMessage !== next.isLatestMessage) return false;
  if (prev.isSessionExpiry !== next.isSessionExpiry) return false;
  // TRAC-518 Phase 8a follow-up: sessionId flips from null to the
  // pregenerated ID once initSession() resolves; without this, the memo
  // swallows that change and starter-prompt impressions never fire on the
  // welcome message. traceId + feedbackId are threaded through to
  // TurnFeedback so they must also trigger a re-render when set.
  if (prev.sessionId !== next.sessionId) return false;
  if (prev.turnNumber !== next.turnNumber) return false;
  if (prev.traceId !== next.traceId) return false;
  // TRAC-631: langfuseTraceUrl arrives on `response_complete` after the
  // fast-path props have already stabilized; without this entry the memo
  // would swallow the prop change and the copy-trace-link button would
  // silently never appear for internal users.
  if (prev.langfuseTraceUrl !== next.langfuseTraceUrl) return false;
  if (prev.feedbackId !== next.feedbackId) return false;
  // TRAC-732: the human-chat band flips IN PLACE — the pending header goes
  // "While we wait" → "Chat with {name} started" (dividerPending), and the rep's
  // real name/photo back-fill onto the divider + relayed messages on agent_named.
  // Without these, the memo swallows those prop changes and the band stays stuck on
  // "While we wait" even though the state updated (the top header, which bypasses this
  // memo via a window event, flips correctly — hence the mismatch).
  if (prev.dividerPending !== next.dividerPending) return false;
  if (prev.agentName !== next.agentName) return false;
  if (prev.agentAvatar !== next.agentAvatar) return false;
  if (prev.richHtml !== next.richHtml) return false;
  // Skip re-render — nothing meaningful changed
  return true;
});
